import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, DatabaseBackup, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const fmtBytes = (b) => {
  if (b == null) return "";
  if (b >= 1048576) return `${(b / 1048576).toLocaleString("it-IT", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(b / 1024)).toLocaleString("it-IT")} KB`;
};

export const BackupStatusCard = () => {
  const [stato, setStato] = useState(null);
  const [starting, setStarting] = useState(false);
  const attesa = useRef(null); // "backup" | "prova" quando aspettiamo un esito avviato da qui

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/backup/stato");
      setStato(data);
      if (!data.in_corso && attesa.current) {
        const tipo = attesa.current;
        attesa.current = null;
        if (tipo === "backup") {
          const u = data.ultimi?.[0];
          if (u?.ok) toast.success(`Backup riuscito: ${u.filename} (${fmtBytes(u.size_backup_bytes)}, ${u.documenti?.toLocaleString("it-IT")} documenti)`);
          else toast.error(`Backup fallito: ${u?.error || "errore sconosciuto"}`);
        } else {
          const p = data.ultima_prova;
          if (p?.ok) toast.success(`Ripristino verificato: ${p.collezioni} collezioni, ${p.documenti_ripristinati?.toLocaleString("it-IT")} documenti (copia ${p.filename})`);
          else toast.error(`Prova ripristino fallita: ${p?.error || "errore sconosciuto"}`);
        }
      }
    } catch { /* silenzioso: la card semplicemente non si aggiorna */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!stato?.in_corso) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [stato?.in_corso, load]);

  if (!stato) return null;

  const avvia = async (url, tipo) => {
    setStarting(true);
    try {
      await api.post(url);
      attesa.current = tipo;
      toast.info(tipo === "backup"
        ? "Backup avviato: continua sul server, l'esito comparirà qui."
        : "Prova di ripristino avviata: l'esito comparirà qui.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Avvio non riuscito.");
    } finally {
      setStarting(false);
    }
  };

  const occupato = Boolean(stato.in_corso) || starting;
  const ultimo = stato.ultimi?.[0];
  const banda = !stato.abilitato && !stato.prova_ripristino_consentita
    ? { cls: "border-amber-200 bg-amber-50 text-amber-900",
        testo: "Il backup notturno è SPENTO in questo ambiente (BACKUP_ENABLED=false)." }
    : stato.salute === "ultimo_fallito"
      ? { cls: "border-red-200 bg-red-50 text-red-900",
          testo: `L'ultimo backup è FALLITO (${fmtDT(ultimo?.at)}): ${ultimo?.error || "errore sconosciuto"}` }
      : stato.salute === "stantio"
        ? { cls: "border-red-200 bg-red-50 text-red-900",
            testo: `Nessun backup riuscito da più di 48 ore (ultimo riuscito: ${fmtDT(stato.ultimo_ok_at)}).` }
        : stato.salute === "mai_eseguito" && !stato.prova_ripristino_consentita
          ? { cls: "border-red-200 bg-red-50 text-red-900",
              testo: "Non risulta NESSUN backup eseguito." }
          : null;

  return (
    <div className="bg-surface border border-border rounded-[10px] p-4" data-testid="backup-status-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <DatabaseBackup size={16} className="text-text-muted" />
          <span className="text-sm font-semibold text-text-primary">Backup del database</span>
          {stato.ultimo_ok_at && (
            <span className="inline-flex items-center gap-1 text-[12px] text-emerald-700" data-testid="backup-last-ok">
              <CheckCircle2 size={13} />
              Ultimo riuscito: {fmtDT(stato.ultimo_ok_at)}
              {stato.ore_da_ultimo_ok != null ? ` (${stato.ore_da_ultimo_ok.toLocaleString("it-IT")} ore fa)` : ""}
            </span>
          )}
        </div>
        {stato.config_mancante?.length > 0 ? (
          <span className="text-[12px] text-text-muted" data-testid="backup-config-missing">
            Configurazione incompleta: mancano {stato.config_mancante.join(", ")}
          </span>
        ) : (
          <div className="flex items-center gap-2">
            {!stato.prova_ripristino_consentita && (
              <Button size="sm" variant="outline" onClick={() => avvia("/admin/backup/esegui", "backup")}
                      disabled={occupato} data-testid="backup-run-btn">
                {occupato ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <RefreshCw size={13} className="mr-1.5" />}
                Esegui backup adesso
              </Button>
            )}
            {stato.prova_ripristino_consentita && (
              <Button size="sm" variant="outline" onClick={() => avvia("/admin/backup/verifica-ripristino", "prova")}
                      disabled={occupato} data-testid="backup-verify-btn">
                {occupato ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <ShieldCheck size={13} className="mr-1.5" />}
                Prova ripristino
              </Button>
            )}
          </div>
        )}
      </div>
      {(stato.ultima_prova || !stato.prova_ripristino_consentita) && (
        <div className="mt-2 text-[12px] text-text-muted" data-testid="backup-restore-note">
          {stato.ultima_prova ? (
            <span data-testid="backup-last-verify">
              Ultima prova di ripristino: {fmtDT(stato.ultima_prova.at)} — {stato.ultima_prova.ok ? "riuscita" : "FALLITA"}
              {stato.ultima_prova.ok && stato.ultima_prova.collezioni
                ? ` (${stato.ultima_prova.collezioni} collezioni, ${stato.ultima_prova.documenti_ripristinati?.toLocaleString("it-IT")} documenti, copia ${stato.ultima_prova.filename})`
                : stato.ultima_prova.error ? ` — ${stato.ultima_prova.error}` : ""}
            </span>
          ) : (
            <span>La prova di ripristino si esegue in preview (credenziale di sola lettura sul bucket).</span>
          )}
        </div>
      )}
      {stato.in_corso && (
        <div className="mt-3 flex items-start gap-2 rounded-[8px] border border-sky-200 bg-sky-50 px-3 py-2.5 text-[13px] font-medium text-sky-900"
             data-testid="backup-inprogress-band">
          <Loader2 size={15} className="mt-0.5 shrink-0 animate-spin" />
          <span>
            {stato.in_corso.tipo === "prova_ripristino" ? "Prova di ripristino" : "Backup"} in corso
            (avviato {fmtDT(stato.in_corso.at)}): continua sul server, l'esito comparirà qui da solo.
          </span>
        </div>
      )}
      {banda && (
        <div className={`mt-3 flex items-start gap-2 rounded-[8px] border px-3 py-2.5 text-[13px] font-medium ${banda.cls}`}
             data-testid="backup-health-band">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>{banda.testo}</span>
        </div>
      )}
      {stato.allarme_ultimo_fallito && (
        <div className="mt-2 flex items-start gap-2 rounded-[8px] border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] font-medium text-red-900"
             data-testid="backup-alarm-failed-band">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>L'email di allarme dell'ultimo esito NON è stata consegnata a nessun destinatario: controllare BACKUP_ALERT_EMAIL (più indirizzi separati da virgola) e la lista di soppressione di Resend.</span>
        </div>
      )}
    </div>
  );
};
